import { useEffect, useState } from 'react';
import {
  CustomModal,
  ModuleView,
  theme,
  Select,
  RichTextEditor,
  Input,
  DeleteModal,
} from '@jadwalh/ui';
import { setErrors } from '@jadwalh/utils';
import { Flex, Button } from '@mantine/core';
import { Wrapper, ProsedureWrappers } from '../styles';
import { useIntl } from 'react-intl';
import { BiPlusCircle } from 'react-icons/bi';
import { ReactComponent as ArrowIcon } from '../../../../assets/images/svg/arrow-export.svg';
import { ReactComponent as EditIcon } from '../../../../assets/images/svg/editPenIcon.svg';
import { ReactComponent as TrashIcon } from '../../../../assets/images/svg/trash-times.svg';
import { Controller, useFormContext } from 'react-hook-form';
import { Mention as TiptapMention } from '@tiptap/extension-mention';
import { getEnumsData } from '../../../../store/enums.slice';
import { useSelector } from 'react-redux';
import suggestion from './suggestions';
import PortalMentionInput from './PortalMentionInput';
import { ErrorMessage } from '@hookform/error-message';
import { useMutation } from '@tanstack/react-query';
import { updateAction, createAction } from '../../../../api/workflow.api';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import instance from '../../../../axios-instance';
import { cleanContent } from '../../../../utils/contentHelpers';
import { FORM_REGEX_VALIDATORS } from '../../../../app-constants/form-validations';

export default function Prosedure({
  procedures,
  emailData,
  refetch,
}: {
  procedures: any;
  emailData: any;
  refetch: () => void;
}) {
  const enumsData = useSelector(getEnumsData);
  const [prosedureData, setProsedureData] = useState<any>(procedures);
  const { formatMessage } = useIntl();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = useFormContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [notificationText, setNotificationText] = useState('');
  const [showNotificationContent, setShowNotificationContent] = useState(false);
  const language = 'ar';
  const procedureTypeWatch = watch('action');
  const [prosedureLabel, setProsedureLabel] = useState('');
  const [currentProcedure, setCurrentProcedure] = useState<any>(null);
  const { id } = useParams();

  const { mutateAsync, isLoading: isActionUpdating } = useMutation({
    mutationKey: ['updateAction', id],
    mutationFn: (data: any) => {
      return editId
        ? updateAction(data, Number(editId))
        : createAction(data, Number(id));
    },
    onSuccess: (response: any) => {
      toast.success(formatMessage({ id: 'newAppointmentAdded' }));
      handleCloseModal();
      setProsedureData(response.action_workflows);
    },
    onError: (error: any) => {
      const errors = error.response.data.errors;
      setErrors(errors, setError);
    },
  });

  const { mutateAsync: deleteAction } = useMutation({
    mutationKey: ['deleteAction', id],
    mutationFn: (id: number) => {
      return instance.delete(`app/workflows/action-workflow/${id}`);
    },
    onSuccess: (response: any) => {
      setProsedureData(response.data.data.action_workflows);
      toast.success(formatMessage({ id: 'deletedSuccessfully' }));
    },
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const resetValues = () => {
    setValue('action', '');
    setValue('send_to', '');
    setValue('sender_provider', '');
    setValue('template_id', '');
    setValue('subject', '');
    setValue('message', '');
    setTitle('');
    setNotificationText('');
  };

  const setFormValues = (prosedure: any) => {
    setValue('action', prosedure.action.toString());
    setValue('send_to', prosedure.send_to?.toString());
    setValue('sender_provider', prosedure.sender_provider);
    setValue('template_id', prosedure.template_id?.toString());
    setValue('message', prosedure.message);
    setValue('subject', prosedure.subject);
    setTitle(prosedure.subject?.toString());
    setNotificationText(prosedure.message?.toString());
    setShowNotificationContent(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditId(null);
    setShowNotificationContent(false);
    clearErrors();
    setTitle('');
    setNotificationText('');
    setProsedureLabel('');
    resetValues();
  };

  const actions = enumsData.enums.actions_enum;

  const getProcedureType = (type: string) => {
    if (type !== '' && actions) {
      return actions.find((item: any) => item.value === type)?.label;
    }
  };

  useEffect(() => {
    setProsedureLabel(getProcedureType(watch('action')) || '');
  }, [watch('action')]);


  const helper = enumsData?.enums[`notification_ar_params_enums`]?.map(
    (param: any, index: number) => ({
      id: index,
      display: `(${param.value})`,
    })
  );


  // Add suggestion configuration
  const mentionSuggestionConfig = {
    char: '@',
    items: ({ query }: { query: string }) => {
      return (
        helper?.filter((item: any) =>
          item.display.toLowerCase().includes(query.toLowerCase())
        ) || []
      );
    },
    command: ({ editor, range, props }: any) => {
      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: 'mention',
            attrs: {
              id: props.id,
              label: props.display,
            },
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run();
    },
    render: () => {
      let component: any;

      return {
        onStart: (props: any) => {
          component = suggestion({
            items: helper || [],
            command: (item: any) => {
              props.command({ id: item.id, display: item.display });
            },
            clientRect: props.clientRect,
          });
        },
        onUpdate: (props: any) => {
          component?.updateProps({
            items: helper || [],
            command: (item: any) => {
              props.command({ id: item.id, display: item.display });
            },
            clientRect: props.clientRect,
          });
        },
        onKeyDown: (props: any) => {
          if (component?.onKeyDown) {
            return component.onKeyDown(props);
          }
          return false;
        },
        onExit: () => {
          component?.destroy();
          component = null;
        },
      };
    },
    allow: ({ editor, range }: any) => {
      return true; // You can add custom logic here to control when mentions are allowed
    },
  };

  const handleEditProcedure = (prosedure: any) => {
    setCurrentProcedure(prosedure);
    setEditId(prosedure.id);
    setValue('action', prosedure.action.toString());
    setValue('send_to', prosedure.send_to?.toString());
    setValue('sender_provider', prosedure.sender_provider);
    setValue('template_id', prosedure.template_id?.toString());
    setValue('message', prosedure.message);
    setValue('subject', prosedure.subject);
    setTitle(prosedure.subject?.toString());
    setNotificationText(prosedure.message?.toString());
    setShowNotificationContent(true);
    handleOpenModal();
  };

  const handleDeleteAction = (id: number) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const addProsedure = (data: any) => {
    mutateAsync({
      action: Number(data.action),
      send_to: data.send_to,
      sender_provider: Number(data.sender_provider),
      subject: title,
      message: notificationText,
    });
  };

  return (
    <ModuleView>
        <Wrapper>
        <Flex direction="column">
          <h2>{formatMessage({ id: 'suitableProsedure' })}</h2>
          {prosedureData && prosedureData.length > 0 ? (
            <Flex direction="column" gap={'8px'}>
              <ul className="question_container">
                {prosedureData.map((prosedure: any, index: number) => (
                  <li
                    className="container"
                    style={{ padding: '8px 16px' }}
                    key={index}
                  >
                    <Flex justify="space-between" align="start">
                      <Flex direction="column" gap={4}>
                        <p>{getProcedureType(prosedure.action.toString())}</p>
                      </Flex>
                      <Flex
                        align="center"
                        gap={16}
                        className="question-actions"
                      >
                        <EditIcon
                          onClick={() => handleEditProcedure(prosedure)}
                          style={{
                            cursor: 'pointer',
                            marginTop: '4px',
                            color: '#25314C',
                          }}
                          // className={`${routesHasUpdate && 'disabled'}`}
                        />
                        <TrashIcon
                          style={{
                            cursor: 'pointer',
                            color: '#25314C',
                          }}
                          onClick={() => handleDeleteAction(prosedure.id)}
                        />
                      </Flex>
                    </Flex>
                  </li>
                ))}
              </ul>
              {/* <div className="hint">
                <p>يرجي مرجعة بيانات الاجراء</p>
              </div> */}
              <Flex justify={'space-between'} align={'center'}>
                {prosedureData && prosedureData.length < 5 ? (
                  <button
                    type="button"
                    className={`text-link`}
                    onClick={handleOpenModal}
                  >
                    <BiPlusCircle />
                    {formatMessage({ id: 'addProsedure' })}
                  </button>
                ) : (
                  <div />
                )}
                <p className="max-prosedure">
                  {formatMessage(
                    { id: 'maxProsedure' },
                    { number: prosedureData.length }
                  )}
                </p>
              </Flex>
            </Flex>
          ) : (
            <Flex
              justify={'center'}
              align={'center'}
              direction={'column'}
              p={'32px'}
              pb={'16px'}
              gap={'16px'}
            >
              <ArrowIcon />
              <p className="desc">{formatMessage({ id: 'whatHappenDescInWorkflowProcedure' })}</p>
              <button
                type="button"
                className="text-link"
                onClick={handleOpenModal}
              >
                <BiPlusCircle />
                <span>{formatMessage({ id: 'addProsedure' })}</span>
              </button>
            </Flex>
          )}
        </Flex>
        {isModalOpen && (
          <CustomModal
            opened={isModalOpen}
            onClose={handleCloseModal}
            withCloseButton={false}
            size="529px"
            closeOnEscape={false}
            header={prosedureLabel || formatMessage({ id: 'addProsedure' })}
          >
            <form id="add-prosedure">
              <ProsedureWrappers>
                <Flex direction="column" gap={'24px'}>
                  {!showNotificationContent && (
                    <Controller
                      control={control}
                      name="action"
                      rules={{
                        required: {
                          value: true,
                          message: `${formatMessage({
                            id: 'requiredField',
                          })}`,
                        },
                      }}
                      render={({ field: { onChange, value } }) => (
                        <Select
                          name="action"
                          searchable={false}
                          label={formatMessage({ id: 'prosedureType' })}
                          placeholder={formatMessage({
                            id: 'chooseType',
                          })}
                          withinPortal={true}
                          data={actions}
                          onChange={(value) => {
                            if (
                              (currentProcedure &&
                                editId &&
                                value !==
                                  currentProcedure?.action?.toString()) ||
                              !editId
                            ) {
                              resetValues();
                            }
                            if (
                              currentProcedure &&
                              editId &&
                              value === currentProcedure?.action?.toString()
                            ) {
                              setFormValues(currentProcedure);
                            }

                            onChange(value);
                            setShowNotificationContent(true);
                            setValue('action', value as string);
                          }}
                          value={value as string}
                        />
                      )}
                    />
                  )}
                </Flex>
                {showNotificationContent && (
                  <Flex direction="column" gap={'16px'}>
                    <Flex direction="column" gap={'10px'}>
                      <Flex direction="column" gap={'10px'}>
                        <Controller
                          control={control}
                          name="sender_provider"
                          rules={{
                            required: {
                              value: true,
                              message: `${formatMessage({
                                id: 'requiredField',
                              })}`,
                            },
                          }}
                          render={({ field: { onChange, value } }) => (
                            <Select
                              name="sender_provider"
                              searchable={false}
                              label={formatMessage({ id: 'sendFromEmail' })}
                              data={
                                emailData?.map((item: any) => ({
                                  label: item.email,
                                  value: item.id,
                                })) || []
                              }
                              onChange={(e) => {
                                onChange(e);
                                clearErrors('sender_provider');
                              }}
                              value={value as string}
                            />
                          )}
                        />
                      </Flex>
                      {procedureTypeWatch === '0' && (
                        <p className="desc">
                          {formatMessage({ id: 'manageUrlFromGmail' })}{' '}
                        </p>
                      )}
                    </Flex>

                    <Flex direction="column" gap={'10px'}>
                      {procedureTypeWatch === '2' && (
                        <Flex direction="column" gap={'10px'}>
                          <Input
                            name="send_to"
                            placeholder="Name@email.com"
                            label={formatMessage({
                              id: 'sendToEmailWithAddress',
                            })}
                            onChange={(e) => {
                              setValue('send_to', e.target.value);
                              clearErrors('send_to');
                            }}
                            registerOptions={{
                              required: {
                                value: true,
                                message: `${formatMessage({
                                  id: 'requiredField',
                                })}`,
                              },
                              pattern: {
                                value: FORM_REGEX_VALIDATORS.validEmail,
                                message: formatMessage({ id: 'invalidEmail' }),
                              },
                            }}
                          />
                        </Flex>
                      )}

                      {/* <Controller
                        control={control}
                        name="template_id"
                        render={({ field: { onChange, value } }) => (
                          <Select
                            name="template"
                            searchable={false}
                            label={formatMessage({ id: 'chooseFromTemplates' })}
                            placeholder={formatMessage({
                              id: 'chooseWay',
                            })}
                            data={procedureTypes}
                            onChange={onChange}
                            value={value as string}
                          />
                        )}
                      /> */}
                      {procedureTypeWatch && (
                        <>
                          <Controller
                            control={control}
                            name="subject"
                            rules={{
                              required: {
                                value: true,
                                message: formatMessage({ id: 'requiredField' }),
                              },
                            }}
                            render={({ field: { onChange, value } }) => (
                              <Flex direction="column" gap={'10px'}>
                                <div
                                  className={`custom_textarea ${
                                    errors.subject ? 'hasError' : ''
                                  }`}
                                  style={{
                                    marginBottom: '16px',
                                  }}
                                >
                                  <label>
                                    <p
                                      style={{
                                        fontSize: '14px',
                                        marginInlineStart: '12px',
                                      }}
                                    >
                                      {formatMessage({ id: 'emailTitle' })}
                                    </p>
                                  </label>
                                  <p
                                    style={{
                                      fontSize: '12px',
                                      marginInlineStart: '8px',
                                      color: '#666666',
                                    }}
                                  >
                                   {formatMessage({ id: 'notification_helper' })}
                                  </p>
                                  <PortalMentionInput
                                    value={title}
                                    onChange={(newValue) => {
                                      const cleanedValue = newValue.startsWith(' ')
                                        ? newValue.slice(1)
                                        : newValue;
                                      setTitle(cleanedValue);
                                      onChange(cleanedValue);
                                      clearErrors('subject');
                                    }}
                                    placeholder=""
                                    mentionItems={helper || []}
                                    style={{
                                      direction: language === 'ar' ? 'rtl' : 'ltr',
                                      textAlign: language === 'ar' ? 'right' : 'left',
                                    }}
                                  />
                                </div>
                                {errors.subject && (
                                  <div className="ErrorMessageStyle">
                                    <ErrorMessage
                                      errors={errors}
                                      name={'subject'}
                                      render={({ message }) => <p>{message}</p>}
                                    />
                                  </div>
                                )}
                              </Flex>
                            )}
                          />
                          <Controller
                            control={control}
                            name="message"
                            rules={{
                              required: {
                                value: true,
                                message: formatMessage({
                                  id: 'requiredField',
                                }),
                              },
                              validate: (value: string) => {
                                if (!value || value.trim() === '') {
                                  const errorMsg = formatMessage({
                                    id: 'requiredField',
                                  });
                                  return errorMsg;
                                }
                                return true;
                              },
                            }}
                            render={({ field: { onChange, value } }) => (
                              <div
                                className={`custom_textarea ${
                                  errors.message ? 'hasError' : ''
                                }`}
                              >
                                <RichTextEditor
                                  name="message"
                                  label={formatMessage({
                                    id: 'notificationText',
                                  })}
                                  placeholder=""
                                  registerOptions={{
                                    required: {
                                      value: true,
                                      message: formatMessage({
                                        id: 'requiredField',
                                      }),
                                    },
                                  }}
                                  helperText={formatMessage({
                                    id: 'notification_helper',
                                  })}
                                  onChange={(content: string) => {
                                    const cleanedContent =
                                      cleanContent(content);
                                    onChange(cleanedContent);
                                    setValue('message', cleanedContent);
                                    setNotificationText(cleanedContent);

                                    if (cleanedContent) {
                                      setTimeout(() => {
                                        clearErrors('message');
                                      }, 0);
                                    }
                                  }}
                                  content={notificationText}
                                  extensions={[
                                    TiptapMention.configure({
                                      HTMLAttributes: {
                                        class: 'mention',
                                      },
                                      renderLabel({ options, node }) {
                                        return node.attrs.label;
                                      },
                                      suggestion: mentionSuggestionConfig,
                                    }),
                                  ]}
                                />
                              </div>
                            )}
                          />
                        </>
                      )}

                      {/* <Flex direction={'column'} gap={'4px'}>
                        {procedureTypeWatch === '1' && (
                          <Checkbox
                            name="confirm_btn"
                            label={formatMessage({ id: 'addConfirmBtn' })}
                          />
                        )}
                        {(procedureTypeWatch === '1' ||
                          procedureTypeWatch === '2') && (
                          <>
                            <Checkbox
                              name="cancel_reschedule_btn"
                              label={formatMessage({
                                id: 'addCancelAndRescheduleButton',
                              })}
                            />
                            <Checkbox
                              name="cancel_policy"
                              label={formatMessage({ id: 'addCancelPolicy' })}
                            />
                          </>
                        )}
                      </Flex> */}
                    </Flex>
                  </Flex>
                )}
              </ProsedureWrappers>
              <Flex
                style={{
                  borderTop: `1px solid ${theme.colours.newOutline}`,
                  padding: '12px 16px',
                }}
                justify={'space-between'}
                align={'center'}
                gap={12}
              >
                <Flex justify={'start'} gap={12}>
                  {showNotificationContent ? (
                    <Button
                      form="add-prosedure"
                      type="button"
                      style={{
                        backgroundColor: theme.colours.blackText,
                        borderRadius: '8px',
                        color: theme.colours.white,
                        minWidth: '80px',
                        height: '32px',
                      }}
                      onClick={() => {
                        handleSubmit(addProsedure)();
                      }}
                      loading={isActionUpdating}
                    >
                      {!isActionUpdating &&
                        (editId !== null
                          ? formatMessage({ id: 'save' })
                          : formatMessage({ id: 'add' }))}
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      style={{
                        backgroundColor: theme.colours.blackText,
                        borderRadius: '8px',
                        color: theme.colours.white,
                        minWidth: '80px',
                        height: '32px',
                      }}
                      onClick={() => {
                        if (procedureTypeWatch) {
                          setShowNotificationContent(true);
                        } else {
                          setError('action', {
                            message: `${formatMessage({
                              id: 'requiredField',
                            })}`,
                          });
                        }
                      }}
                    >
                      {formatMessage({ id: 'next' })}
                    </Button>
                  )}
                  <Button
                    onClick={handleCloseModal}
                    style={{
                      backgroundColor: theme.colours.white,
                      borderRadius: '8px',
                      color: theme.colours.blackText,
                      border: `1px solid ${theme.colours.newOutline}`,
                      minWidth: '80px',
                      height: '32px',
                    }}
                  >
                    {formatMessage({ id: 'cancel' })}
                  </Button>
                </Flex>
                {showNotificationContent && (
                  <Button
                    type="button"
                    style={{
                      backgroundColor: theme.colours.white,
                      borderRadius: '8px',
                      color: theme.colours.blackText,
                      border: `1px solid ${theme.colours.newOutline}`,
                      minWidth: '80px',
                      maxWidth: '80px',
                      height: '32px',
                    }}
                    onClick={() => {
                      setShowNotificationContent(false);
                    }}
                  >
                    {formatMessage({ id: 'back' })}
                  </Button>
                )}
              </Flex>
            </form>
          </CustomModal>
        )}
        {showDeleteModal && (
          <DeleteModal
            showDeleteModal={showDeleteModal}
            setShowDeleteModal={setShowDeleteModal}
            message="deleteActionConfirm"
            submit={() => deleteAction(deleteId as number)}
          />
        )}
        </Wrapper>
    </ModuleView>
  );
}
